import Button from '@/components/UI/Button';
import DebounceInput from '@/components/UI/DebounceInput';
import Icon from '@/components/UI/Icon';
import { _isNil, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import { FC, useCallback, useState, useEffect, type FocusEvent } from 'react';
import './IndividualSaveRepeater.scss';
import { ExtendedMetadataItem } from './IndividualSaveRepeater.utils';
import { METADATA_OPTION_MAX_LENGTH } from './IndividualSaveRepeater.constants';

interface OptionsProps {
  row: ExtendedMetadataItem;
  loading: boolean;
  disabled?: boolean;
  onOptionsChange?: (itemId: ID, currentOptions: string[]) => void;
}

const Options: FC<OptionsProps> = ({ row, loading, disabled = false, onOptionsChange }) => {
  const [localOptions, setLocalOptions] = useState<string[]>([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  useEffect(() => {
    if (!hasLocalChanges) {
      setLocalOptions(row.options ?? []);
    }
  }, [row.options, hasLocalChanges]);

  const getCurrentOptions = useCallback(() => {
    return hasLocalChanges ? localOptions : (row.options ?? []);
  }, [hasLocalChanges, localOptions, row.options]);

  const clearLocalChanges = useCallback(() => {
    setLocalOptions(row.options ?? []);
    setHasLocalChanges(false);
  }, [row.options]);

  const getLocalOptionsForSync = useCallback(() => {
    const raw = hasLocalChanges ? localOptions : (row.options ?? []);
    return raw.map((o) => o.trim());
  }, [hasLocalChanges, localOptions, row.options]);

  useEffect(() => {
    if (row.id) {
      (row as unknown as Record<string, unknown>)._clearOptions = clearLocalChanges;
      (row as unknown as Record<string, unknown>)._getLocalOptions = getLocalOptionsForSync;
    }
  }, [clearLocalChanges, getLocalOptionsForSync, row.id]);

  const validateNoDuplicateOptions = useCallback((options: string[]) => {
    const seen = new Set<string>();
    const duplicates = new Set<number>();

    options.forEach((option, index) => {
      const normalizedValue = option.trim().toLowerCase();

      if (!normalizedValue) {
        return;
      }

      if (seen.has(normalizedValue)) {
        duplicates.add(index);

        options.forEach((prevOption, prevIndex) => {
          if (prevIndex < index && prevOption.trim().toLowerCase() === normalizedValue) {
            duplicates.add(prevIndex);
          }
        });
      } else {
        seen.add(normalizedValue);
      }
    });

    return duplicates;
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const index = event.target.getAttribute('data-id');
      const value = event.target.value;

      if (_notNil(index)) {
        const currentOptions = getCurrentOptions();
        const newOptions = [...currentOptions];
        newOptions[Number(index)] = value;

        setLocalOptions(newOptions);
        setHasLocalChanges(true);

        if (onOptionsChange && row.id) {
          onOptionsChange(row.id, newOptions);
        }
      }
    },
    [getCurrentOptions, onOptionsChange, row.id]
  );

  const handleOptionBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const indexAttr = event.target.getAttribute('data-id');
      if (_isNil(indexAttr)) {
        return;
      }

      const trimmed = String(event.target.value ?? '').trim();
      const idx = Number(indexAttr);
      const currentOptions = getCurrentOptions();
      const newOptions = [...currentOptions];
      newOptions[idx] = trimmed;

      setLocalOptions(newOptions);
      setHasLocalChanges(true);

      if (onOptionsChange && row.id) {
        onOptionsChange(row.id, newOptions);
      }
    },
    [getCurrentOptions, onOptionsChange, row.id]
  );

  const handleAdd = useCallback(() => {
    const currentOptions = getCurrentOptions();
    const newOptions = [...currentOptions, ''];

    setLocalOptions(newOptions);
    setHasLocalChanges(true);

    if (onOptionsChange && row.id) {
      onOptionsChange(row.id, newOptions);
    }
  }, [getCurrentOptions, onOptionsChange, row.id]);

  const handleRemove = useCallback(
    (index: number) => {
      const currentOptions = getCurrentOptions();
      const newOptions = currentOptions.filter((_, i) => i !== index);

      setLocalOptions(newOptions);
      setHasLocalChanges(true);

      if (onOptionsChange && row.id) {
        onOptionsChange(row.id, newOptions);
      }
    },
    [getCurrentOptions, onOptionsChange, row.id]
  );

  const getFieldErrorMessage = (index?: number): string => {
    if (_isNil(index)) {
      return '';
    }

    const currentOptions = getCurrentOptions();
    const currentOptionValue = currentOptions[index] ?? '';

    if (String(currentOptionValue ?? '').trim() === '') {
      return "Option can't be blank";
    }
    if (String(currentOptionValue ?? '').length > METADATA_OPTION_MAX_LENGTH) {
      return `Option can't exceed ${METADATA_OPTION_MAX_LENGTH} characters`;
    }
    return '';
  };

  const hasFieldError = (index?: number): boolean => {
    if (_isNil(index)) {
      return false;
    }

    const currentOptions = getCurrentOptions();
    const currentOptionValue = currentOptions[index] ?? '';

    return (
      String(currentOptionValue ?? '').trim() === '' ||
      String(currentOptionValue ?? '').length > METADATA_OPTION_MAX_LENGTH
    );
  };

  const optionsToRender = getCurrentOptions();
  const duplicateIndices = validateNoDuplicateOptions(optionsToRender);

  return (
    <div className="ml5">
      <div className="ui__repeater__children mb2">
        <label className={`pl4 ${disabled ? 'dark-gray' : ''}`}>Options</label>
        {optionsToRender.map((o, index) => {
          const hasDuplicate = duplicateIndices.has(index) && o.trim() !== '';
          return (
            <div key={index} className="ui__repeater__child flex items-center pv2">
              <DebounceInput
                handleChange={handleChange}
                inputProps={{
                  className:
                    `z-5 relative options-input ${hasFieldError(index) ? 'input__error' : ''} ${disabled ? 'o-60' : ''} ${hasDuplicate ? 'input__error' : ''}`
                      .replace(/\s+/g, ' ')
                      .trim(),
                  'data-tooltip-content': hasDuplicate
                    ? 'Duplicate options are not allowed'
                    : getFieldErrorMessage(index),
                  'data-tooltip-id': 'global-tooltip-id',
                  value: o,
                  'data-id': index,
                  type: 'text',
                  disabled: loading || disabled,
                  onBlur: handleOptionBlur,
                }}
              />
              {!disabled && (
                <Button
                  soft
                  onClick={() => handleRemove(index)}
                  disabled={optionsToRender.length === 1 || loading}
                  className="ml3 option-remove-button"
                >
                  <Icon icon="close" width="10" height="10" className="dark-gray" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
      {!disabled && (
        <Button
          disabled={loading}
          onClick={handleAdd}
          className="ml4 mb3 br-pill f6"
          icon="add_new"
          paleBlue
          testId="add-option-button"
        >
          <span>Add option</span>
        </Button>
      )}
    </div>
  );
};

export default Options;
